/*
 *  -----------------------------------------------------------------------
 *    <copyright file="actions-grid-v2.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useState, useEffect } from 'react';
import { orderBy } from '@progress/kendo-data-query';

import ActionsEmailDialog from '@components/central-monitoring/actions-email-dialog/actions-email-dialog';
import ObservationDetailTemplate from '@components/central-monitoring/observation-detail-template';
import { GridColumnDateFilterV2, GridColumnMenuActiveFilterV2 } from '../core/grid-filters-v2';
import { ExtraMenuItemPosition } from '../core/grid-header/grid-menu/grid-menu.interface';
import ControlObservation from './control-observation';
import { restructuredUpdatedCreatedBy } from '@utils-clean/update-users-array';
import { useBoolean, useTranslation, useGridCells } from 'hooks';
import { handleTimeInDates, reorderArray } from 'utils/filter';
import NotificationService from 'utils/notification';
import ExpandableGridV2 from 'views/components/core/expandable/grid-v2/index';
import InformationDialog from 'views/components/core/information-dialog/information-dialog';
import {
    riskObservations_OpraQueries_riskObservations_RiskObservation,
    riskObservations_OpraQueries_riskObservations_RiskObservation_actions_RiskAction,
} from '@queries/generated/legacy-risk-assessment.generated';

interface IActionsGridV2 {
    study: any;
    actions: riskObservations_OpraQueries_riskObservations_RiskObservation_actions_RiskAction[];
    observations: riskObservations_OpraQueries_riskObservations_RiskObservation[];
    editObservation: (any: any) => any;
    title?: string;
    rowActions?: any;
    canEdit?: boolean;
}

const ActionsGridV2 = ({
    study,
    actions,
    observations,
    editObservation,
    rowActions,
    title,
    canEdit,
}: IActionsGridV2) => {
    const { t } = useTranslation();

    const showEmailDialog = useBoolean(false);
    const showInformationDialog = useBoolean(false);
    const showDeletedItems = useBoolean(false);

    const [data, setData] = useState([]);
    const [currentActions, setCurrentActions] = useState([]);
    const [informationDialogText, setInformationDialogText] = useState(null);
    const { DateCell, MaxLengthCell, YesNoCell, ActiveInactiveCell } = useGridCells();

    // It is cleaning all checkbox before you leave this page
    // useEffect(() => {
    //     if (data?.length && isFirstTime.value) {
    //         isFirstTime.setFalse();
    //         const newData = restructuredUpdatedCreatedBy(data);
    //         setData(
    //             newData.map((item) => ({
    //                 ...item,
    //                 checked: false,
    //                 _isCompletedDateEmpty: Boolean(item.completedDate),
    //             })),
    //         );
    //     }
    // }, [data]);

    useEffect(() => {
        let filteredData = [...actions].map((item) => ({
            ...item,
            _isCompletedDateEmpty: Boolean(item.completedDate),
        }));
        const newData = restructuredUpdatedCreatedBy(filteredData);
        // we need to have this logic when we have two fields for sorting which are dates or when the first sorting field is date
        let arrayToSort = newData.slice();
        arrayToSort = handleTimeInDates(arrayToSort, ['updated']);
        arrayToSort =
            orderBy(arrayToSort, [
                { field: '_isCompletedDateEmpty', dir: 'asc' },
                { field: 'updated', dir: 'desc' },
            ]) ?? [];
        filteredData = reorderArray(filteredData, arrayToSort);
        // end of logic
        setData(filteredData);
    }, [actions]);

    const onActionsEmailDialogClose = (action: string) => {
        showEmailDialog.setFalse();
        if (action === 'done') NotificationService.showNotification(t('ra:successfulSentEmail'));
        if (action === 'error') NotificationService.showNotification(t('errorMessage'));
        setData(data.map((item: any) => ({ ...item, checked: false })));
    };

    const canOpen = (
        checkedRows: riskObservations_OpraQueries_riskObservations_RiskObservation_actions_RiskAction[],
    ) => {
        if (!checkedRows) {
            return false;
        }
        return checkedRows.every(
            (dataItem: riskObservations_OpraQueries_riskObservations_RiskObservation_actions_RiskAction) =>
                dataItem.ownerId === checkedRows[0].ownerId,
        );
    };

    const handleSelection = (
        oldChecked: riskObservations_OpraQueries_riskObservations_RiskObservation_actions_RiskAction[],
        newChecked: riskObservations_OpraQueries_riskObservations_RiskObservation_actions_RiskAction[],
    ): boolean => {
        if (newChecked.some((dataItem) => !dataItem.owner?.email)) {
            setInformationDialogText(t('ra:ownerHasNotEmail'));
            showInformationDialog.setTrue();
            return false;
        }
        if (newChecked.some((dataItem) => !dataItem.owner?.active)) {
            setInformationDialogText(t('ra:ownerIsNotActive'));
            showInformationDialog.setTrue();
            return false;
        }
        if (!canOpen(newChecked)) {
            setInformationDialogText(t('ra:youCanChooseActionOnlySameOwner'));
            showInformationDialog.setTrue();
            return false;
        }
        return true;
    };

    return (
        <>
            <InformationDialog
                open={showInformationDialog.value}
                onClose={() => showInformationDialog.setFalse()}
                text={informationDialogText}
                heading={t('ra:actionEmail')}
            />
            <ActionsEmailDialog
                study={study}
                risks={[]}
                actions={currentActions}
                observations={observations}
                open={showEmailDialog.value}
                close={onActionsEmailDialogClose}
                query="SEND_CM_ACTION_EMAIL"
                canEdit={canEdit}
            />
            <ExpandableGridV2
                handleSelection={handleSelection}
                title={title}
                customKey="manage-actions-grid"
                id="actionsGrid"
                items={data}
                rowActions={rowActions}
                groupable
                showCheckbox={canEdit}
                showPager
                reorderable
                scrollable="scrollable"
                pageable={false}
                resizable
                showDeletedItems={showDeletedItems}
                detail={(
                    action: riskObservations_OpraQueries_riskObservations_RiskObservation_actions_RiskAction | any,
                ) => {
                    if (action.observationId) {
                        return (
                            <ControlObservation studyId={study.id}>
                                {(handleDeleteObs: Function, handleRestoreObs: Function) => (
                                    <ObservationDetailTemplate
                                        observation={action.observation}
                                        editObservation={editObservation}
                                        canDelete
                                        canEdit={canEdit}
                                        isRA={false}
                                        handleDeleteObs={handleDeleteObs}
                                        handleRestoreObs={handleRestoreObs}
                                        showDeletedItems={showDeletedItems.value}
                                        study={study}
                                    />
                                )}
                            </ControlObservation>
                        );
                    }
                    return null;
                }}
                columns={[
                    {
                        field: 'deleted',
                        id: 'observation-associated-deleted',
                        label: t('deleted'),
                        minWidth: 134,
                        showByDefault: false,
                        cells: { data: YesNoCell },
                        filterUi: GridColumnMenuActiveFilterV2,
                    },
                    {
                        label: t('cm:site'),
                        field: '_siteName',
                        id: 'cm-action-site',
                        minWidth: 108,
                        locked: true,
                        lockedByDefault: true,
                    },
                    {
                        label: t('ra:description'),
                        field: 'description',
                        id: 'cm-action-description',
                        minWidth: 174,
                        locked: true,
                        lockedByDefault: true,
                        cells: { data: MaxLengthCell(50) },
                    },
                    {
                        label: t('ra:requestedDate'),
                        id: 'cm-action-requested-date',
                        field: 'requestDate',
                        minWidth: 156,
                        filter: 'date',
                        cells: { data: DateCell },
                        filterUi: GridColumnDateFilterV2,
                    },
                    {
                        label: t('cm:actionTaken'),
                        field: 'actionTaken',
                        id: 'cm-action-actionTaken',
                        minWidth: 174,
                        cells: { data: MaxLengthCell(50) },
                    },
                    {
                        label: t('cm:rootCause'),
                        field: 'rootCause',
                        id: 'cm-action-rootCause',
                        minWidth: 174,
                        cells: { data: MaxLengthCell(50) },
                    },
                    {
                        label: t('result'),
                        field: 'result',
                        id: 'cm-action-result',
                        minWidth: 174,
                        cells: { data: MaxLengthCell(50) },
                    },
                    {
                        label: t('ra:expectedCompletionDate'),
                        id: 'cm-action-expected-completion-date',
                        field: 'expectedCompleteDate',
                        filter: 'date',
                        minWidth: 200,
                        cells: { data: DateCell },
                        filterUi: GridColumnDateFilterV2,
                    },
                    {
                        label: t('subject:completedDate'),
                        id: 'cm-action-completed-date',
                        field: 'completedDate',
                        filter: 'date',
                        minWidth: 158,
                        cells: { data: DateCell },
                        filterUi: GridColumnDateFilterV2,
                    },
                    {
                        label: t('owner'),
                        field: '_ownerLabel',
                        id: 'cm-action-owner',
                        minWidth: 126,
                        cells: {
                            data: ActiveInactiveCell(
                                (dataItem: any) => dataItem._owner?.active === false,
                                (dataItem: any) => dataItem._ownerLabel,
                            ),
                        },
                    },
                    {
                        label: t('ra:updatedBy'),
                        field: '_updatedByString',
                        id: 'cm-action-updatedby',
                        minWidth: 140,
                    },
                    {
                        label: t('ra:updatedDate'),
                        field: 'updated',
                        id: 'cm-action-updated',
                        filter: 'date',
                        minWidth: 144,
                        cells: { data: DateCell },
                        filterUi: GridColumnDateFilterV2,
                    },
                    {
                        field: '_deleteReason',
                        id: 'observation-associated-delete-reason',
                        label: t('cm:deleteReasonLabel'),
                        showByDefault: false,
                        minWidth: 174,
                        cells: { data: MaxLengthCell(50) },
                    },
                    {
                        field: '_deleteDate',
                        id: 'observation-associated-delete-date',
                        label: t('cm:deleteDate'),
                        showByDefault: false,
                        filter: 'date',
                        minWidth: 138,
                        cells: { data: DateCell },
                        filterUi: GridColumnDateFilterV2,
                    },
                    {
                        field: '_deletedBy',
                        id: 'observation-associated-deleted-by',
                        label: t('cm:deletedBy'),
                        showByDefault: false,
                        minWidth: 138,
                    },
                    {
                        label: t('ra:createdDate'),
                        field: 'created',
                        id: 'cm-action-created',
                        showByDefault: false,
                        filter: 'date',
                        minWidth: 180,
                        cells: { data: DateCell },
                        filterUi: GridColumnDateFilterV2,
                    },
                    {
                        label: t('ra:createdBy'),
                        field: '_createdByString',
                        id: 'cm-action-createdby',
                        showByDefault: false,
                        minWidth: 180,
                    },
                    {
                        field: 'recordId',
                        label: 'ID',
                        minWidth: 120,
                        id: 'action-id',
                        filter: 'numeric',
                        showByDefault: false,
                    },
                ]}
                extraMenuItems={
                    canEdit
                        ? [
                              {
                                  position: ExtraMenuItemPosition.expandMenu,
                                  expandMenuItems: [
                                      {
                                          label: 'uam:emailSelected',
                                          icon: ['fal', 'envelope'],
                                          requireSelection: true,
                                          enabled: (checkedRows: any) => !!checkedRows.length,
                                          onClick: (checkedRows: any) => {
                                              setCurrentActions(checkedRows);
                                              showEmailDialog.setTrue();
                                          },
                                          testId: 'email-selected',
                                      },
                                  ],
                              },
                          ]
                        : []
                }
            />
        </>
    );
};

export default ActionsGridV2;
